"use client";

import { CalendarDays } from "lucide-react";
import { useId, useRef, useState, type FormEvent } from "react";

import { useBookCallPopup } from "@/components/booking/CcBookCallPopupProvider";
import { devSectionAttr } from "@/lib/dev/section-attr";
import { bookCallOriginFromElement } from "@/lib/integrations/book-call-popup-origin";

import type { SeoPageCopy } from "./seo-page-copy";
import { normalizeWebsite } from "./seo-website-input";

const CC_SEO_FOOTER_CLOSING_CTA_TITLE_ID = "cc-seo-footer-closing-cta-title";

type CcSeoFooterClosingCtaProps = {
  copy: SeoPageCopy["cta"];
};

/** Centered SEO audit form inset for the Odysseus footer closing band. */
export function CcSeoFooterClosingCta({ copy }: CcSeoFooterClosingCtaProps) {
  const bookCall = useBookCallPopup();
  const submitRef = useRef<HTMLButtonElement>(null);
  const ids = useId().replace(/[^a-zA-Z0-9_-]/g, "");
  const [website, setWebsite] = useState("");
  const [error, setError] = useState(false);

  const onSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!normalizeWebsite(website)) {
      setError(true);
      return;
    }
    setError(false);
    const trigger = submitRef.current;
    const normalizedWebsite = normalizeWebsite(website);
    bookCall.open(
      trigger ? bookCallOriginFromElement(trigger) : undefined,
      {
        variant: "seo",
        prefill: normalizedWebsite ? { website: normalizedWebsite } : undefined,
      },
    );
  };

  return (
    <section
      className="cc-home-closing-cta cc-home-closing-cta--seo"
      aria-labelledby={CC_SEO_FOOTER_CLOSING_CTA_TITLE_ID}
      {...devSectionAttr("seo-footer-cta")}
    >
      <div className="cc-home-closing-cta__frame" data-cc-nav="dark">
        <div className="cc-home-closing-cta__container">
          <div className="cc-home-closing-cta__intro">
            <h2 id={CC_SEO_FOOTER_CLOSING_CTA_TITLE_ID} className="cc-home-closing-cta__title">
              {copy.title}
            </h2>
            <p className="cc-home-closing-cta__subtitle">{copy.text}</p>
          </div>

          <form data-analytics-form="seo_audit_prefill" className="cc-seo-cta__form cc-home-closing-cta__seo-form" noValidate onSubmit={onSubmit}>
            <div className="cc-seo-cta__row">
              <input
                id={`${ids}-website`}
                className="cc-seo-cta__input"
                type="text"
                inputMode="url"
                autoComplete="url"
                spellCheck={false}
                aria-label={copy.placeholder}
                placeholder={copy.placeholder}
                value={website}
                onChange={(event) => {
                  setWebsite(event.target.value);
                  if (error) setError(false);
                }}
                aria-invalid={error}
                aria-describedby={error ? `${ids}-error ${ids}-note` : `${ids}-note`}
              />
              <button
                ref={submitRef}
                type="submit"
                className="cc-seo-cta__button cc-seo-cta__button--brand"
                data-book-call-variant="seo"
              >
                <CalendarDays className="cc-seo-cta__button-icon" strokeWidth={1.8} aria-hidden="true" />
                {copy.button}
              </button>
            </div>
            <p
              id={`${ids}-error`}
              className={`cc-seo-cta__error${error ? " cc-seo-cta__error--visible" : ""}`}
              role={error ? "alert" : undefined}
              aria-hidden={error ? undefined : true}
            >
              {copy.error}
            </p>
            <p id={`${ids}-note`} className="cc-seo-cta__note">
              {copy.note}
            </p>
          </form>
        </div>
      </div>
    </section>
  );
}
